import type { CSSProperties, MouseEvent } from 'react';
import { XYCHART_DIMMED_OPACITY } from './xychart-constants';
import {
  XYCHART_SERIES_TYPE,
  type XYChartLayout,
  type XYChartLinePoint,
  type XYChartSeriesLayout,
} from './xychart-types';
import { getFontFamily } from '@/theme/typography';

const SAFE_ID_REGEX = /[^a-zA-Z0-9_-]/g;

export interface MeasuredLineLengths {
  [seriesIndex: number]: number;
}

export interface XYChartLineMarkerSelection {
  lastPoint: XYChartLinePoint | null;
  hoveredPoint: XYChartLinePoint | null;
  showLastDot: boolean;
}

export interface XYChartCategoryHitZone {
  categoryIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CreateSafeSvgIdInput {
  uniqueId: string;
}

export function createSafeSvgId({ uniqueId }: CreateSafeSvgIdInput): string {
  return uniqueId.replace(SAFE_ID_REGEX, '');
}

interface CreateCanvasSvgStyleInput {
  layout: XYChartLayout;
}

export function createCanvasSvgStyle({ layout }: CreateCanvasSvgStyleInput): CSSProperties {
  return {
    width: layout.width,
    height: layout.height,
    display: 'block',
    overflow: 'visible',
    fontFamily: getFontFamily(),
  };
}

interface SelectTypedSeriesInput {
  series: XYChartSeriesLayout[];
}

export function selectBarSeries({ series }: SelectTypedSeriesInput): XYChartSeriesLayout[] {
  return series.filter((seriesLayout) => seriesLayout.type === XYCHART_SERIES_TYPE.Bar);
}

export function selectLineSeries({ series }: SelectTypedSeriesInput): XYChartSeriesLayout[] {
  return series.filter((seriesLayout) => seriesLayout.type === XYCHART_SERIES_TYPE.Line);
}

interface SelectSeriesOpacityInput {
  hoveredSeriesIndex: number | null;
  seriesIndex: number;
}

export function selectSeriesOpacity({
  hoveredSeriesIndex,
  seriesIndex,
}: SelectSeriesOpacityInput): number {
  if (hoveredSeriesIndex == null || hoveredSeriesIndex === seriesIndex) return 1;
  return XYCHART_DIMMED_OPACITY;
}

interface SelectFocusBandInput {
  hoveredCategoryIndex: number | null;
  categoryCenters: number[];
}

export function selectFocusBand({
  hoveredCategoryIndex,
  categoryCenters,
}: SelectFocusBandInput): number | null {
  if (hoveredCategoryIndex == null) return null;
  const center = categoryCenters[hoveredCategoryIndex];
  if (center == null) return null;
  return center;
}

interface ResolveHoveredCategoryIndexInput {
  event: MouseEvent<SVGSVGElement>;
  layout: XYChartLayout;
}

export function resolveHoveredCategoryIndex({
  event,
  layout,
}: ResolveHoveredCategoryIndexInput): number | null {
  if (layout.categories.length === 0) return null;

  const svg = event.currentTarget;
  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;

  const ctm = svg.getScreenCTM();
  if (!ctm) return null;

  const local = point.matrixTransform(ctm.inverse());
  const axisPosition = layout.horizontal
    ? local.y - layout.margins.top
    : local.x - layout.margins.left;

  return layout.categoryCenters.reduce(
    (nearest, center, index) => {
      const distance = Math.abs(center - axisPosition);
      if (distance < nearest.distance) {
        return { index, distance };
      }
      return nearest;
    },
    { index: 0, distance: Number.POSITIVE_INFINITY },
  ).index;
}

interface SelectLineMarkersInput {
  linePoints: XYChartLinePoint[];
  hoveredCategoryIndex: number | null;
}

export function selectLineMarkers({
  linePoints,
  hoveredCategoryIndex,
}: SelectLineMarkersInput): XYChartLineMarkerSelection {
  const lastPoint = linePoints[linePoints.length - 1] ?? null;
  const hoveredPoint =
    hoveredCategoryIndex == null
      ? null
      : linePoints.find((point) => point.categoryIndex === hoveredCategoryIndex) ?? null;

  return {
    lastPoint,
    hoveredPoint,
    showLastDot: lastPoint != null && hoveredPoint == null,
  };
}

interface SelectCategoryHitZonesInput {
  layout: XYChartLayout;
}

export function selectCategoryHitZones({
  layout,
}: SelectCategoryHitZonesInput): XYChartCategoryHitZone[] {
  const axisLength = layout.horizontal ? layout.plotHeight : layout.plotWidth;
  const stepWidth = axisLength / Math.max(1, layout.categories.length);

  return layout.categories.flatMap((_category, categoryIndex) => {
    const center = layout.categoryCenters[categoryIndex];
    if (center == null) return [];

    if (layout.horizontal) {
      return [
        {
          categoryIndex,
          x: 0,
          y: center - stepWidth / 2,
          width: layout.plotWidth,
          height: stepWidth,
        },
      ];
    }

    return [
      {
        categoryIndex,
        x: center - stepWidth / 2,
        y: 0,
        width: stepWidth,
        height: layout.plotHeight,
      },
    ];
  });
}
